import { useCallback, useEffect, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import log from 'loglevel';

import { getRegistration } from '../serviceWorkerRegistration';

export const PERMISSION_STATE = {
  PROMPT: 'prompt',
  GRANTED: 'granted',
  DENIED: 'denied',
  UNSUPPORTED: 'unsupported',
} as const;

type PermissionState = (typeof PERMISSION_STATE)[keyof typeof PERMISSION_STATE];

export const SERVICE_WORKER_MESSAGE_TYPES = {
  // NOTE: These values are also hardcoded in apps/web/public/service-worker.js
  // which cannot import TS modules, so keep both values in sync manually.
  PUSH_SUBSCRIPTION_CHANGED: 'PUSH_SUBSCRIPTION_CHANGED',
  CLEAR_BADGE: 'CLEAR_BADGE',
} as const;

export const PUSH_NOTIFICATION_EVENTS = {
  PUSH_SUBSCRIPTION_CHANGE: 'pushsubscriptionchange',
} as const;

export const DOM_EVENTS = {
  VISIBILITY_CHANGE: 'visibilitychange',
} as const;

export const VISIBILITY_STATE = {
  VISIBLE: 'visible',
} as const;

function mapNotificationPermission(
  permission: NotificationPermission,
): PermissionState {
  if (permission === 'default') return PERMISSION_STATE.PROMPT;
  return permission;
}

const SUBSCRIBE_TO_PUSH = gql`
  mutation SubscribeToPush($subscription: PushSubscriptionInput!, $userAgent: String) {
    subscribeToPush(subscription: $subscription, userAgent: $userAgent)
  }
`;

const UNSUBSCRIBE_FROM_PUSH = gql`
  mutation UnsubscribeFromPush($endpoint: String!) {
    unsubscribeFromPush(endpoint: $endpoint)
  }
`;

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permissionState, setPermissionState] = useState<PermissionState>(
    PERMISSION_STATE.UNSUPPORTED,
  );
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [subscribeToPushMutation] = useMutation(SUBSCRIBE_TO_PUSH);
  const [unsubscribeFromPushMutation] = useMutation(UNSUBSCRIBE_FROM_PUSH);

  const subscribeToPushRef = useRef(subscribeToPushMutation);
  subscribeToPushRef.current = subscribeToPushMutation;

  const unsubscribeFromPushRef = useRef(unsubscribeFromPushMutation);
  unsubscribeFromPushRef.current = unsubscribeFromPushMutation;

  const syncSubscriptionToBackend = useCallback(
    async (subscriptionJson: PushSubscriptionJSON) => {
      try {
        await subscribeToPushRef.current({
          variables: {
            subscription: {
              endpoint: subscriptionJson.endpoint,
              keys: {
                p256dh: subscriptionJson.keys?.p256dh,
                auth: subscriptionJson.keys?.auth,
              },
            },
            userAgent: navigator.userAgent,
          },
        });
      } catch (error) {
        log.error('Failed to sync push subscription to backend:', error);
      }
    },
    [],
  );

  const createSubscription = useCallback(
    async (registration: ServiceWorkerRegistration) => {
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        log.warn('VAPID public key not configured');
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      await syncSubscriptionToBackend(subscription.toJSON());
      setIsSubscribed(true);
    },
    [syncSubscriptionToBackend],
  );

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPermissionState(PERMISSION_STATE.UNSUPPORTED);
      return;
    }

    setPermissionState(mapNotificationPermission(Notification.permission));

    const checkExistingSubscription = async () => {
      try {
        const registration = await getRegistration();
        if (!registration) return;

        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        log.error('Failed to check push subscription:', error);
      }
    };

    checkExistingSubscription();
  }, []);

  useEffect(() => {
    const notifyServiceWorker = async () => {
      try {
        const registration = await getRegistration();
        registration?.active?.postMessage({
          type: SERVICE_WORKER_MESSAGE_TYPES.CLEAR_BADGE,
        });
      } catch (error) {
        log.error('Failed to notify service worker to clear badge:', error);
      }
    };

    const clearBadge = () => {
      if (document.visibilityState !== VISIBILITY_STATE.VISIBLE) return;

      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {
          // Badge API may throw if not supported in this context
        });
      }

      notifyServiceWorker();
    };

    document.addEventListener(DOM_EVENTS.VISIBILITY_CHANGE, clearBadge);
    clearBadge();

    return () =>
      document.removeEventListener(DOM_EVENTS.VISIBILITY_CHANGE, clearBadge);
  }, []);

  useEffect(() => {
    const handleSubscriptionChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      log.info('Push subscription changed, re-subscribing');
      syncSubscriptionToBackend(customEvent.detail);
    };

    window.addEventListener(
      PUSH_NOTIFICATION_EVENTS.PUSH_SUBSCRIPTION_CHANGE,
      handleSubscriptionChange,
    );
    return () => {
      window.removeEventListener(
        PUSH_NOTIFICATION_EVENTS.PUSH_SUBSCRIPTION_CHANGE,
        handleSubscriptionChange,
      );
    };
  }, [syncSubscriptionToBackend]);

  useEffect(() => {
    if (permissionState === PERMISSION_STATE.GRANTED && !isSubscribed) {
      const resubscribeIfNeeded = async () => {
        try {
          const registration = await getRegistration();
          if (!registration) return;

          const existing = await registration.pushManager.getSubscription();
          if (existing) {
            await syncSubscriptionToBackend(existing.toJSON());
            setIsSubscribed(true);
            return;
          }

          await createSubscription(registration);
        } catch (error) {
          log.error('Failed to resubscribe:', error);
        }
      };

      resubscribeIfNeeded();
    }
  }, [permissionState, isSubscribed, createSubscription]);

  const subscribe = useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(mapNotificationPermission(permission));

      if (permission !== 'granted') {
        return;
      }

      const registration = await getRegistration();
      if (!registration) {
        log.error('Service worker not registered');
        return;
      }

      await createSubscription(registration);
    } catch (error) {
      log.error('Failed to subscribe to push notifications:', error);
    }
  }, [createSubscription]);

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();

      await unsubscribeFromPushRef.current({
        variables: { endpoint },
      });

      setIsSubscribed(false);
    } catch (error) {
      log.error('Failed to unsubscribe from push notifications:', error);
    }
  }, []);

  return {
    permissionState,
    isSubscribed,
    subscribe,
    unsubscribe,
  };
}
